import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { fmtUsd } from '../../constants/journey';
import { getLiveRailwayState, resolveStations } from './s05LiveRailway';

const frame = (key) => ({
  image: `/assets/cinematic-v3/s05-${key}-arrival.webp`,
  mobile: `/assets/cinematic-v3/s05-${key}-arrival-mobile.webp`,
});

const SHOT_COPY = [
  { key: 'bonk', title: 'SHIBA FOUNDRY', detail: 'FIRST STOP. DOG ALREADY WORKING.' },
  { key: 'wif', title: 'WINDCUT RIDGE', detail: 'HAT STAYED ON.' },
  { key: 'cupsey', title: 'PUMP PLATFORM', detail: 'CUP FULL. PUMPS LOUD.' },
  { key: 'popcat', title: 'ECHO QUARRY', detail: 'MOUTH OPEN. TRACK CLEAR.' },
  { key: 'fartcoin', title: 'GASWORKS MARSH', detail: "PRESSURE NORMAL. AIR ISN'T." },
  { key: 'jimothy', title: 'NIGHT SALVAGE', detail: 'HE STOLE THE NIGHT SHIFT.' },
  { key: 'bome', title: 'ARCHIVE CITY', detail: 'MEMES GOT A LIBRARY. BAD SIGN.' },
  { key: 'ket', title: 'OBSERVATORY JCT.', detail: 'CAT AT ALTITUDE.' },
  { key: 'ansem', title: 'IRON MOUNTAIN', detail: 'BULL MADE THE GRADE.' },
  { key: 'troll', title: 'LAST BRIDGE', detail: 'THE INTERNET BUILT THE FINAL SPAN.' },
];

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export const S05CinematicJourney = ({ reducedMotion, state, configuredStations }) => {
  const galleryRef = useRef(null);
  const navigationRef = useRef(null);
  const dragRef = useRef(null);
  const [viewedStationIndex, setViewedStationIndex] = useState(0);

  const stations = useMemo(() => resolveStations(configuredStations), [configuredStations]);
  const shots = useMemo(() => [
    {
      key: 'aerial',
      image: '/assets/cinematic-v3/s05-world-master.webp',
      mobile: '/assets/cinematic-v3/s05-world-master-mobile.webp',
      title: '',
      detail: '',
    },
    ...SHOT_COPY.map((shot, index) => ({
      ...shot,
      ...frame(shot.key),
      station: stations[index],
      number: index + 1,
    })),
  ], [stations]);

  const live = useMemo(() => getLiveRailwayState(state, stations), [state, stations]);
  const permanentPercent = clamp((Number(state?.routeProgress) || 0) * 100, 0, 100);
  const nextProgress = live.nextStation ? Number(live.nextStation.routeProgress ?? live.nextStation.progress) * 100 : 100;
  const lowerProgress = live.actualStation ? Number(live.actualStation.routeProgress ?? live.actualStation.progress) * 100 : 0;
  const livePercent = lowerProgress + Math.max(0, nextProgress - lowerProgress) * live.livePressure;

  const centerNavigation = useCallback((index) => {
    if (index <= 0 || !navigationRef.current) return;
    const active = navigationRef.current.querySelector(`[data-route-index="${index}"]`);
    active?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
  }, [reducedMotion]);

  const setViewed = useCallback((index) => {
    setViewedStationIndex(index);
    centerNavigation(index);
  }, [centerNavigation]);

  const scrollToShot = useCallback((index) => {
    const gallery = galleryRef.current;
    const target = gallery?.querySelector(`[data-s05-index="${index}"]`);
    if (!gallery || !target) return;
    gallery.scrollTo({ left: target.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    setViewed(index);
  }, [reducedMotion, setViewed]);

  const step = useCallback((direction) => {
    scrollToShot(clamp(viewedStationIndex + direction, 0, shots.length - 1));
  }, [scrollToShot, shots.length, viewedStationIndex]);

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      const mostVisible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (mostVisible?.intersectionRatio >= 0.55) setViewed(Number(mostVisible.target.dataset.s05Index));
    }, { root: gallery, threshold: [0.55, 0.72, 0.9] });
    gallery.querySelectorAll('[data-s05-index]').forEach((slide) => observer.observe(slide));
    return () => observer.disconnect();
  }, [setViewed]);

  const onPointerDown = (event) => {
    if (event.pointerType === 'touch' || !galleryRef.current) return;
    dragRef.current = { id: event.pointerId, x: event.clientX, y: event.clientY, left: galleryRef.current.scrollLeft, locked: false };
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    const gallery = galleryRef.current;
    if (!drag || !gallery || drag.id !== event.pointerId) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.locked) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) >= Math.abs(dx)) {
        dragRef.current = null;
        return;
      }
      drag.locked = true;
      gallery.setPointerCapture?.(event.pointerId);
    }
    event.preventDefault();
    gallery.scrollLeft = drag.left - dx;
  };

  const endPointerDrag = (event) => {
    if (dragRef.current?.id === event.pointerId) {
      galleryRef.current?.releasePointerCapture?.(event.pointerId);
      dragRef.current = null;
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      scrollToShot(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      scrollToShot(shots.length - 1);
    }
  };

  const actualName = live.actualStation?.place || state?.stationName || 'THOMAS DEPOT';
  const actualTicker = live.actualStation?.ticker || 'DEPOT';
  const liveStatus = live.liveAvailable ? fmtUsd(state?.currentMcapUsd) : '—';

  return (
    <section className="s05-gallery-section relative bg-[#0d1115]" data-testid="chapter-s05" data-audio-scene="railway" aria-label="Solana meme railway communities">
      <div
        ref={galleryRef}
        className="s05-gallery"
        role="region"
        aria-roledescription="carousel"
        aria-label="Community railway. Swipe or drag horizontally; scroll down to continue."
        tabIndex="0"
        onKeyDown={onKeyDown}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointerDrag}
        onPointerCancel={endPointerDrag}
        data-testid="community-gallery"
      >
        {shots.map((shot, index) => (
          <article
            key={shot.key}
            data-s05-shot={shot.key}
            data-s05-index={index}
            className="s05-gallery-slide"
            aria-label={index === 0 ? 'Solana railway world overview' : `${shot.station?.ticker}, stop ${index} of 10`}
          >
            <picture>
              <source media="(max-width: 640px)" srcSet={shot.key === 'troll' ? '/assets/cinematic-v3/s05-troll-arrival-mobile-v2.webp' : shot.mobile} />
              <img
                src={shot.key === 'troll' ? '/assets/cinematic-v3/s05-troll-arrival-v2.webp' : shot.image}
                alt=""
                className="absolute inset-0 h-full w-full object-cover object-left sm:object-center"
                draggable="false"
                loading={index < 2 ? 'eager' : 'lazy'}
                decoding="async"
                fetchPriority={index < 2 ? 'high' : 'auto'}
              />
            </picture>
            <div className="s05-cinema-scrim s05-cinema-scrim-left" />
            {shot.station && (
              <div className="s05-gallery-copy">
                <div className="s05-rail-marker" aria-hidden="true">
                  <span className="s05-rail-ticker">{shot.station.ticker}</span>
                  <span className="s05-rail-line" />
                  <span className="s05-rail-number">{shot.number}</span>
                  <span className="s05-rail-total">/ 10</span>
                </div>
                <h2>{shot.title}</h2>
                <p>{shot.detail}</p>
              </div>
            )}
          </article>
        ))}
      </div>

      <div className="s05-gallery-controls" aria-label="Gallery controls">
        <button type="button" onClick={() => step(-1)} disabled={viewedStationIndex === 0} aria-label="Previous railway view"><ChevronLeft size={20} /></button>
        <span aria-live="polite">{viewedStationIndex === 0 ? 'WORLD' : `${viewedStationIndex} / 10`}</span>
        <button type="button" onClick={() => step(1)} disabled={viewedStationIndex === shots.length - 1} aria-label="Next railway view"><ChevronRight size={20} /></button>
      </div>

      <div className="s05-live-console">
        <div className="mx-auto max-w-[1440px] px-3 pb-4 sm:px-6 sm:pb-5">
          <div className="s05-location-strip">
            <button type="button" className="s05-location-now" onClick={() => scrollToShot(live.actualStationIndex)} data-testid="where-is-thomas">
              <span className="s05-location-label">WHERE IS THOMAS NOW?</span>
              <span className="s05-location-name">{actualTicker} · {actualName}</span>
            </button>
            <div className="s05-live-readout" aria-live="polite">
              <span><b>LIVE MC</b>{liveStatus}</span>
              {live.nextStation ? (
                <><span><b>NEXT</b>{live.nextStation.ticker} · {fmtUsd(live.nextStation.threshold)}</span><span><b>TO GO</b>{live.remainingMcap === null ? '—' : fmtUsd(live.remainingMcap)}</span></>
              ) : <span className="s05-line-cleared">FULL LINE CLEARED</span>}
            </div>
          </div>

          <div className="s05-track-meter" aria-label={`Permanent route ${Math.round(permanentPercent)} percent; live pressure ${Math.round(live.livePressure * 100)} percent`}>
            <span className="s05-track-live" style={{ width: `${livePercent}%` }} />
            <span className="s05-track-ath" style={{ width: `${permanentPercent}%` }} />
          </div>

          <nav ref={navigationRef} className="s05-route-nav" aria-label="Ten Solana meme railway stops">
            {stations.map((station, index) => {
              const routeIndex = index + 1;
              const reached = routeIndex <= live.actualStationIndex;
              const viewing = routeIndex === viewedStationIndex;
              const next = routeIndex === live.actualStationIndex + 1;
              return (
                <button
                  key={station.id}
                  type="button"
                  data-route-index={routeIndex}
                  data-route-state={viewing ? 'viewing' : reached ? 'reached' : next ? 'next' : 'future'}
                  className="s05-route-stop"
                  onClick={() => scrollToShot(routeIndex)}
                  aria-current={viewing ? 'true' : undefined}
                  aria-label={`${station.ticker}, stop ${routeIndex} of 10, target ${fmtUsd(station.threshold)}`}
                >
                  <span className="s05-route-number">{routeIndex}<small>/10</small></span>
                  <span className="s05-route-ticker">{station.ticker}</span>
                  <span className="s05-route-target">{fmtUsd(station.threshold)}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    </section>
  );
};
